import { useEffect, useMemo, useState } from "react";
import { fetchTripByIdWithGooglePathApi } from "api/trips";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNotificationContext } from "contexts/NotificationContext";
import { ITripDetailResponse } from "models/trips";
import { Spin } from "antd";
import pickupIcon from "assets/images/pickup.png";
import dropOffIcon from "assets/images/drop-off.png";
import moment from "moment";

// Create static icons once
const PickupMapIcon = new L.Icon({
  iconUrl: pickupIcon,
  iconSize: [40, 30],
  iconAnchor: [20, 30],
});

const DropOffMapIcon = new L.Icon({
  iconUrl: dropOffIcon,
  iconSize: [40, 30],
  iconAnchor: [20, 30],
});

export default function TripRoute() {
  const id: string = useParams().id!;
  const [data, setData] = useState<ITripDetailResponse>();
  const [currentLocation, setCurrentLocation] = useState<{
    lat: number; lng: number;
  }>({
    lat: 13.496494229233356,
    lng: 39.47494624044569,
  });
  const { showError } = useNotificationContext();
  type TripLocation = ITripDetailResponse["locations"][number];
  type LocationPoint = TripLocation & { order: number };

  type LocationGroup = {
    lat: number;
    lng: number;
    points: LocationPoint[];
  };

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  const fetchData = async () => {
    const { data, error } = await fetchTripByIdWithGooglePathApi(id);
    if (data?.data) {
      data.data.locations = data.data.google_paths;
      data.data.locations.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

      setData(data.data);
      if (data.data.locations.length > 0)
        setCurrentLocation({
          lat: data.data.locations[0].latitude,
          lng: data.data.locations[0].longitude,
        });
    } else showError(error!.message);
  };

  const aggregatedLocations = useMemo<LocationGroup[]>(() => {
    if (!data?.locations) return [];

    const grouped = new Map<string, LocationGroup>();

    data.locations.forEach((point, index) => {
      const enrichedPoint = { ...point, order: index + 1 } as LocationPoint;
      const key = `${point.latitude.toFixed(6)}_${point.longitude.toFixed(6)}`;
      const existing = grouped.get(key);

      if (existing) {
        existing.points.push(enrichedPoint);
      } else {
        grouped.set(key, {
          lat: point.latitude,
          lng: point.longitude,
          points: [enrichedPoint],
        });
      }
    });

    return Array.from(grouped.values()).map((group) => ({
      ...group,
      points: [...group.points].sort((a, b) => a.order - b.order),
    }));
  }, [data?.locations]);

  const createGroupIcon = (group: LocationGroup) => {
    const hasMultiplePoints = group.points.length > 1;
    const firstOrder = group.points[0].order;
    const lastOrder = group.points[group.points.length - 1].order;
    const labelText = hasMultiplePoints
      ? `${firstOrder}-${lastOrder}`
      : firstOrder.toString();

    // Custom circle icon with number
    return L.divIcon({
      className: "custom-div-icon",
      html: `<div style="background-color: ${hasMultiplePoints ? '#d83b01' : '#111827'}; color: white; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${labelText}</div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });
  };

  const polylinePositions = data?.locations.map((value) => [value.latitude, value.longitude] as [number, number]) ?? [];

  return (
    <>
      {data ? (
        <MapContainer
          center={[currentLocation.lat, currentLocation.lng]}
          zoom={16}
          style={{ width: "100%", height: "100vh", zIndex: 0 }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {data.locations.length > 0 && (
            <Marker
              icon={PickupMapIcon}
              position={[data.locations[0].latitude, data.locations[0].longitude]}
            />
          )}

          {aggregatedLocations.map((group, key) => (
            <Marker
              key={`${group.lat}-${group.lng}-${key}`}
              position={[group.lat, group.lng]}
              icon={createGroupIcon(group)}
            >
              <Popup>
                <div className="text-base min-w-[200px]">
                  <span className="font-semibold">Pinned points:</span>{" "}
                  {group.points.length}
                  <br />
                  <span className="font-semibold">lat:</span> {" "}
                  {group.lat}
                  <br />
                  <span className="font-semibold">lng:</span> {" "}
                  {group.lng}
                  <br />
                  <span className="font-semibold">Order span:</span>{" "}
                  {group.points.length > 1
                    ? `${group.points[0].order} - ${group.points[
                      group.points.length - 1
                    ].order
                    }`
                    : `#${group.points[0].order}`}
                  <div className="mt-2 max-h-40 overflow-y-auto text-sm bg-gray-50 p-2 rounded">
                    {group.points.map((point) => (
                      <div key={point.id}>
                        #{point.order} – {" "}
                        {moment(point.created_at).format("HH:mm:ss")}
                      </div>
                    ))}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
          {data.locations.length > 1 && (
            <Marker
              icon={DropOffMapIcon}
              position={[data.locations[data.locations.length - 1].latitude, data.locations[data.locations.length - 1].longitude]}
            />
          )}

          <Polyline
            positions={polylinePositions}
            color="#581845"
            weight={4}
            opacity={1.0}
          />
        </MapContainer>
      ) : (
        <div className="w-full h-full grow flex items-center justify-center">
          <Spin size="large" />
        </div>
      )}
    </>
  );
}
